/**
 * Resume-after-reload for the wizard, in this browser.
 *
 * Deliberately client-side. The answers have no home on the platform until the
 * workspace exists — `POST /onboarding/profile/` is addressed by `organization`,
 * and the wizard is what produces that organization — so there is nothing to
 * PATCH a half-filled form into. `/onboarding/journeys/` is no help either: its
 * steps are the checklist's (`invite_users`, `roles`, …), not this form's.
 *
 * The consequence is the honest one: a resume works in the browser that wrote
 * it and nowhere else. Replacing this with a server-side draft needs an endpoint
 * keyed by the *user* rather than by a workspace.
 */
import type { OnboardingDraft } from './types'

const KEY = 'skalx.onboarding.draft'

/**
 * Partial by design: the stored JSON was written by whichever build of the
 * wizard the user last visited, so callers must normalize it rather than trust
 * the type. `preset` and `cleanWorkspace` from before the industry rework are
 * exactly the case this survives — unknown keys are layered over the current
 * defaults and the missing ones simply stay empty.
 */
export const readDraft = (): Partial<OnboardingDraft> | null => {
  try {
    const raw = window.localStorage.getItem(KEY)

    return raw ? (JSON.parse(raw) as Partial<OnboardingDraft>) : null
  } catch {
    return null
  }
}

export const writeDraft = (draft: OnboardingDraft): void => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(draft))
  } catch {
    /* private mode / quota — resuming is a nicety, not a requirement */
  }
}

export const clearDraft = (): void => {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* nothing to clean up */
  }
}
